import { Fragment, useEffect, useRef } from 'react';
import { InAppNotifications } from '../../components/general/InAppNotifications';
import { useAutoFollowDroneSound } from '../../hooks/useAutoFollowDroneSound';
import { useFocusVehicle } from '../../hooks/useFocusVehicle';
import { useFollowedVehicleStore } from '../../store/useFollowedVehicleStore';
import { useSelectedGcsConfigsStore } from '../../store/useSelectedGcsConfigsStore';
import { useSelectedVehicleStore } from '../../store/useSelectedVehicleStore';
import CentersPanel from './centers-panel/CentersPanel';
import { GcsConnectionStateListener } from './GcsConnectionStateListener';
import { GcsForeignAircraftRiskVolumeListener } from './GcsForeignAircraftRiskVolumeListener';
import { GcsUnitSystemMismatchListener } from './GcsUnitSystemMismatchListener';
import { GcsVehicleFailureModeListener } from './GcsVehicleFailureModeListener';
import { InitialMapFocusListener } from './InitialMapFocusListener';
import FlightMap from './map/FlightMap';
import MissionVisibilityButton from './MissionVisibilityButton';
import OverviewButton from './OverviewButton';
import SidePanel from './side-panel/SidePanel';
import { UssConnectionStateListener } from './UssConnectionStateListener';
import VehiclePanel from './vehicle-panel/VehiclePanel';
import type { MapRef } from 'react-map-gl/mapbox';

function DashboardPage() {
  const flightMapRef = useRef<MapRef>(null);
  const focusVehicle = useFocusVehicle(flightMapRef);
  useAutoFollowDroneSound();
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);
  const clearSelectedVehicle = useSelectedVehicleStore((state) => state.clearSelectedVehicle);
  const selectedConfigs = useSelectedGcsConfigsStore((state) => state.selectedConfigs);
  const isAutoFollowEnabled = useFollowedVehicleStore((state) => state.isAutoFollowEnabled);
  const setFollowedVehicle = useFollowedVehicleStore((state) => state.setFollowedVehicle);

  useEffect(() => {
    if (!isAutoFollowEnabled || !selectedVehicle) {
      return;
    }

    setFollowedVehicle({
      gcsId: selectedVehicle.gcsId,
      vehicleId: selectedVehicle.vehicleId
    });
  }, [isAutoFollowEnabled, selectedVehicle, setFollowedVehicle]);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <GcsConnectionStateListener />
      <GcsUnitSystemMismatchListener />
      <UssConnectionStateListener />
      <InitialMapFocusListener mapRef={flightMapRef} />
      {selectedConfigs.map((config) => (
        <Fragment key={config.id}>
          <GcsVehicleFailureModeListener gcsId={config.id} />
          <GcsForeignAircraftRiskVolumeListener gcsId={config.id} />
        </Fragment>
      ))}

      {/* Full Screen Map */}
      <FlightMap ref={flightMapRef} />

      {/* Floating Left SidePanel */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--panel-gap)',
          left: 'var(--panel-gap)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: 'var(--side-panel-width)',
          height: 'calc(100vh - var(--panel-gap) - var(--panel-gap))'
        }}
      >
        <SidePanel />
      </div>

      {/* Vehicle Panel - Below Overview Button */}
      {selectedVehicle && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(var(--panel-gap) + 40px + var(--panel-gap))',
            left: 'calc(var(--panel-gap) + var(--side-panel-width) + var(--panel-gap))',
            width: 'var(--vehicle-panel-width)',
            zIndex: 1000
          }}
        >
          <VehiclePanel
            vehicleId={selectedVehicle.vehicleId}
            gcsId={selectedVehicle.gcsId}
            onClose={clearSelectedVehicle}
            onFocusVehicle={() => focusVehicle(selectedVehicle.gcsId, selectedVehicle.vehicleId)}
          />
        </div>
      )}

      {/* Overview Button - Top, right of side panel */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--panel-gap)',
          left: 'calc(var(--panel-gap) + var(--side-panel-width) + var(--panel-gap))',
          zIndex: 1000
        }}
      >
        <OverviewButton mapRef={flightMapRef} />
      </div>

      {/* Mission Visibility Button - Right of Overview Button */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--panel-gap)',
          left: 'calc(var(--panel-gap) + var(--side-panel-width) + var(--panel-gap) + 40px + var(--panel-gap))',
          zIndex: 1000
        }}
      >
        <MissionVisibilityButton />
      </div>

      {/* Floating Right Rail */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--panel-gap)',
          right: 'var(--panel-gap)',
          bottom: 'var(--panel-gap)',
          zIndex: 1000,
          pointerEvents: 'none',
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr)',
          gap: '16px'
        }}
      >
        <div
          style={{
            justifySelf: 'end',
            pointerEvents: 'auto',
            width: 'var(--centers-panel-width)'
          }}
        >
          <CentersPanel flightMapRef={flightMapRef} />
        </div>

        <div
          style={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pointerEvents: 'none'
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            <InAppNotifications />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
